import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiDownload, FiUpload, FiCheck, FiX, FiFileText } from 'react-icons/fi';
import api from '../../services/api';

const STATUS_OPTIONS = ['Draft', 'Open', 'Paid', 'Void'];

// One house bill is one set of books. GoFreight has always worked this way —
// its settled master OI-26050021 carries five houses and eleven documents, and
// every document hangs off a house; the master's own panel is empty. MyLogistic
// listed all of them in one flat table instead, so OI-26080224 showed two
// consignees' invoices, two agents' notes and two ocean-freight costs stacked
// together and the operator had to tell them apart by reading party names.
//
// `hbl_no` on the charge is what separates them, and the shipment is still one
// shipment per MASTER bill (operator 2026-09-16: 一个MBL建一单…如果有多个HBL，
// 则都建在这个MBL下面…只不过 accounting 的部分，每一个HBL建一个accounting part).
const panelBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'rgba(255,255,255,.16)', color: '#fff',
  border: '1px solid rgba(255,255,255,.35)', borderRadius: 6,
  padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

// Truck and misc have no house bills; ocean spells it hbls/hbl_no and air
// spells it hawbs/hawb_no. A module with no houses must render exactly as it
// did before — no sidebar, no scoping, nothing to click.
const HOUSE_PATH = {
  ocean_import: 'ocean-import', ocean_export: 'ocean-export',
  air_import: 'air-import', air_export: 'air-export',
};

const AccountingTab = ({ shipmentId, shipmentType = 'truck' }) => {
  const navigate = useNavigate();
  const [charges, setCharges] = useState([]);
  const [houses, setHouses] = useState([]);
  const [mblNo, setMblNo] = useState('');
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(true);
  const [memo, setMemo] = useState('');
  const [includeDraft, setIncludeDraft] = useState(true);
  const [showLoadTP, setShowLoadTP] = useState(false);
  const [tpSearch, setTpSearch] = useState('');
  const [tpResults, setTpResults] = useState([]);
  const [loadingTP, setLoadingTP] = useState(false);

  // Invoice upload state
  const invoiceInputRef = useRef(null);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceParsing, setInvoiceParsing] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceCreating, setInvoiceCreating] = useState(false);

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF invoice file');
      return;
    }
    setInvoiceParsing(true);
    setInvoiceModal(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/parse-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setInvoiceData(res.data.data);
      } else {
        toast.error(res.data.error || 'Failed to parse invoice');
        setInvoiceModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to parse invoice');
      setInvoiceModal(false);
    } finally {
      setInvoiceParsing(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = '';
    }
  };

  const handleInvoiceFieldChange = (field, value) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  };

  const handleChargeAmountChange = (index, value) => {
    setInvoiceData(prev => {
      const charges = [...prev.charges];
      charges[index] = { ...charges[index], amount: parseFloat(value) || 0 };
      return { ...prev, charges, total_amount: charges.reduce((s, c) => s + (c.amount || 0), 0) };
    });
  };

  const handleChargeDescChange = (index, value) => {
    setInvoiceData(prev => {
      const charges = [...prev.charges];
      charges[index] = { ...charges[index], description: value };
      return { ...prev, charges };
    });
  };

  const removeChargeLine = (index) => {
    setInvoiceData(prev => {
      const charges = prev.charges.filter((_, i) => i !== index);
      return { ...prev, charges, total_amount: charges.reduce((s, c) => s + (c.amount || 0), 0) };
    });
  };

  const addChargeLine = () => {
    setInvoiceData(prev => ({
      ...prev,
      charges: [...prev.charges, { description: '', amount: 0 }],
    }));
  };

  const createAPFromInvoice = async () => {
    if (!invoiceData || invoiceData.charges.length === 0) {
      toast.error('No charges to import');
      return;
    }
    setInvoiceCreating(true);
    try {
      // Create AP charge header
      const chargeRes = await api.post(`/charges/shipment/${shipmentId}`, {
        charge_type: 'AP',
        shipment_type: shipmentType,
        invoice_no: invoiceData.invoice_no || '',
        party: invoiceData.vendor || '',
        vendor: invoiceData.vendor || '',
        vendor_invoice_no: invoiceData.invoice_no || '',
        invoice_date: invoiceData.invoice_date || '',
        due_date: invoiceData.due_date || '',
        cost: invoiceData.total_amount || 0,
        status: 'Open',
        invoice_received: true,
      });

      if (chargeRes.data?.id) {
        // Create each charge line item
        for (let i = 0; i < invoiceData.charges.length; i++) {
          const ch = invoiceData.charges[i];
          if (ch.amount > 0 || ch.description) {
            await api.post(`/charge-items/charge/${chargeRes.data.id}`, {
              freight_description: ch.description || 'Charge',
              pc: 'C',
              unit: 'SHIPMENT',
              volume: 1,
              rate: ch.amount || 0,
              amount: ch.amount || 0,
              status: 'Open',
              sort_order: i + 1,
            });
          }
        }

        toast.success(`AP created: ${invoiceData.charges.length} charge(s), total $${invoiceData.total_amount.toFixed(2)}`);
        setInvoiceModal(false);
        setInvoiceData(null);
        loadData(); // Refresh charges
      }
    } catch (error) {
      console.error('Create AP error:', error);
      toast.error('Failed to create AP charge');
    } finally {
      setInvoiceCreating(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!shipmentId) return;
    try {
      // shipment_type or the tab shows another module's shipment with the
      // same id — every module numbers its shipments from 1.
      const res = await api.get(`/charges/shipment/${shipmentId}`,
        { params: { shipment_type: shipmentType } });
      setCharges(res.data);

      const path = HOUSE_PATH[shipmentType];
      if (!path) { setHouses([]); return; }
      const s = await api.get(`/${path}/${shipmentId}`);
      const hs = (s.data?.hbls || s.data?.hawbs || [])
        .filter(h => (h.hbl_no || h.hawb_no || '').trim());
      setHouses(hs);
      setMblNo(s.data?.mbl_no || s.data?.mawb_no || '');
      setScope(prev => (prev || (hs.length ? String(hs[0].id) : '')));
    } catch (error) {
      console.error('Failed to load accounting data:', error);
    } finally {
      setLoading(false);
    }
  }, [shipmentId, shipmentType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const houseNo = (h) => (h?.hbl_no || h?.hawb_no || '').trim();
  const scoped = houses.length > 0;
  // One house bill is not a split. Naming it names the shipment, so a charge
  // without an hbl_no is that house's charge — the arrival notice already
  // prints master-level charges on every house, which with one house is the
  // same thing. Showing them in a Master panel above an empty house panel
  // split one set of books in half for no reason.
  const multiHouse = houses.length > 1;
  const currentHouse = houses.find(h => String(h.id) === String(scope));
  const currentNo = houseNo(currentHouse);

  const sum = (rows, k) => rows.reduce((s, c) => s + (Number(c[k]) || 0), 0);
  const everything = charges.filter(c => includeDraft || c.status !== 'Draft');
  const onHouse = (c) => String(c.hbl_no || '').trim();

  // Master level is what carries no house bill. On a shipment kept the way
  // GoFreight keeps OI-26050021 this panel is empty — every one of its eleven
  // documents hangs off a house — so anything appearing here is worth seeing
  // rather than hiding: a charge with no hbl_no prints on EVERY house's
  // arrival notice.
  const masterRows = multiHouse ? everything.filter(c => !onHouse(c)) : everything;
  const houseRows = (h) => {
    if (!multiHouse) return everything;
    const no = houseNo(h).toUpperCase();
    return everything.filter(c => onHouse(c).toUpperCase() === no);
  };
  const currentRows = currentHouse ? houseRows(currentHouse) : [];

  // GoFreight's master panel shows the WHOLE shipment's profit, not just what
  // is booked at master level — 65.00 on OI-26050021, which is house 248's
  // credit. So the number on top is the shipment's bottom line.
  const shipmentProfit = sum(everything, 'revenue') - sum(everything, 'cost');

  const perHouse = (h) => {
    const rows = houseRows(h);
    return { count: rows.length, profit: sum(rows, 'revenue') - sum(rows, 'cost') };
  };

  // A charge raised while one house is in view belongs to that house. Without
  // this the new charge lands master level, shows up on every house's arrival
  // notice, and blocks the next automated run for its siblings.
  const goToNewCharge = (chargeType, hblNo = '') => {
    const q = [`shipmentId=${shipmentId}`, `type=${chargeType}`,
               `shipmentType=${shipmentType}`];
    if (hblNo) q.push(`hbl=${encodeURIComponent(hblNo)}`);
    navigate(`/charge/new?${q.join('&')}`);
  };

  const updateCharge = async (id, field, value) => {
    setCharges(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    try {
      await api.put(`/charges/${id}`, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
      loadData();
    }
  };

  const deleteCharge = async (id) => {
    try {
      await api.delete(`/charges/${id}`);
      setCharges(prev => prev.filter(c => c.id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Trade partner search for default freight
  const searchTP = async (query) => {
    setTpSearch(query);
    if (query.length < 1) { setTpResults([]); return; }
    try {
      const res = await api.get(`/trade-partners/search?q=${encodeURIComponent(query)}`);
      setTpResults(res.data || []);
    } catch { setTpResults([]); }
  };

  const loadDefaultFreight = async (tp) => {
    try {
      setLoadingTP(true);
      const res = await api.get(`/trade-partners/${tp.id}/default-charges`);
      const defaults = res.data || [];
      if (defaults.length === 0) {
        toast.info(`No default charges found for ${tp.name}`);
        setShowLoadTP(false);
        setLoadingTP(false);
        return;
      }

      // Create charges from defaults
      let created = 0;
      for (const dc of defaults) {
        const chargeType = dc.charge_section === 'AR' ? 'AR' : dc.charge_section === 'AP' ? 'AP' : 'DC';
        try {
          // Create the charge header
          const chargeRes = await api.post('/charges', {
            shipment_id: shipmentId,
            charge_type: chargeType,
            shipment_type: shipmentType,
            party: tp.name,
            description: `Default from ${tp.code}`,
            revenue: chargeType === 'AR' ? (dc.amount || 0) : 0,
            cost: chargeType === 'AP' ? (dc.amount || 0) : 0,
            status: 'Draft',
          });

          // Create charge item line
          if (chargeRes.data?.id) {
            await api.post('/charge-items', {
              charge_id: chargeRes.data.id,
              freight_code: dc.freight_code || '',
              freight_description: dc.freight_description || '',
              pc: chargeType === 'AP' ? 'C' : 'P',
              unit: dc.unit || 'UNIT',
              volume: dc.volume || 1,
              rate: dc.rate || 0,
              amount: dc.amount || 0,
              agent_amount: dc.agent_amount || 0,
              status: 'Open',
            });
          }
          created++;
        } catch (err) {
          console.error('Failed to create charge from default:', err);
        }
      }

      toast.success(`Loaded ${created} default charge(s) from ${tp.name}`);
      setShowLoadTP(false);
      setTpSearch('');
      setTpResults([]);
      loadData(); // Refresh
    } catch (error) {
      toast.error('Failed to load default charges');
    } finally {
      setLoadingTP(false);
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  }

  if (!shipmentId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        <p>Please save the shipment first.</p>
      </div>
    );
  }

  const renderChargeRow = (charge) => {
    const balance = (Number(charge.revenue) || 0) - (Number(charge.cost) || 0);
    return (
      <tr key={charge.id}>
        <td>
          <span className="invoice-link" onClick={() => navigate(`/charge/${charge.id}`)}>
            {charge.invoice_no || charge.vendor_invoice_no || `#${charge.id}`}
          </span>
        </td>
        <td>
          <input
            type="text"
            className="cell-input"
            value={charge.party || charge.bill_to || charge.vendor || charge.agent_name || ''}
            onChange={(e) => updateCharge(charge.id, 'party', e.target.value)}
            placeholder="Party name"
            style={{ cursor: 'pointer' }}
            onClick={(e) => { if (!e.target.value) navigate(`/charge/${charge.id}`); }}
          />
        </td>
        <td className="col-number">
          <span className={Number(charge.revenue) > 0 ? 'amount-positive' : 'amount-zero'}>
            {formatCurrency(charge.revenue)}
          </span>
        </td>
        <td className="col-number">
          <span className={Number(charge.cost) > 0 ? 'amount-positive' : 'amount-zero'}>
            {formatCurrency(charge.cost)}
          </span>
        </td>
        <td className={`col-number ${balance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
          {formatCurrency(balance)}
        </td>
        <td>
          <select
            className="cell-select status-select"
            value={charge.status || 'Draft'}
            onChange={(e) => updateCharge(charge.id, 'status', e.target.value)}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
        <td>
          <input
            type="date"
            className="cell-date"
            value={charge.post_date ? charge.post_date.split('T')[0] : ''}
            onChange={(e) => updateCharge(charge.id, 'post_date', e.target.value)}
          />
        </td>
        <td>
          <input
            type="date"
            className="cell-date"
            value={charge.invoice_date ? charge.invoice_date.split('T')[0] : ''}
            onChange={(e) => updateCharge(charge.id, 'invoice_date', e.target.value)}
          />
        </td>
        <td className="col-action">
          <button className="btn-action-delete" onClick={() => deleteCharge(charge.id)} title="Delete">
            <FiTrash2 />
          </button>
        </td>
      </tr>
    );
  };

  // One panel per set of books, stacked the way GoFreight stacks them: the
  // master on top, always on screen, and below it the house the sidebar has
  // selected. The two never share a table, which is the whole point — reading
  // two consignees' invoices out of one list is what the operator was doing.
  const Panel = ({ kind, title, subtitle, extra, tint, warn, rows,
                   profitLabel, profitValue, onCreate, docs }) => {
    const ar = rows.filter(c => c.charge_type === 'AR');
    const dc = rows.filter(c => c.charge_type === 'DC');
    const ap = rows.filter(c => c.charge_type === 'AP');
    const rev = sum(rows, 'revenue');
    const cost = sum(rows, 'cost');
    const bal = rev - cost;
    const profit = profitValue === null || profitValue === undefined ? bal : profitValue;
    const pct = cost !== 0 ? (profit / cost) * 100 : 0;
    const margin = rev !== 0 ? (profit / rev) * 100 : 0;
    const head = tint || (warn ? '#dc2626' : '#334155');

    const Section = ({ label, list, empty }) => (
      <>
        <tr className="section-label-row">
          <td className="section-label" rowSpan={Math.max(list.length, 1) + 1}>
            <div className="section-label-text">{label}</div>
          </td>
        </tr>
        {list.length === 0 ? (
          <tr className="empty-section-row">
            <td colSpan="8" className="empty-section-cell">{empty}</td>
          </tr>
        ) : list.map(renderChargeRow)}
      </>
    );

    return (
      <div style={{ margin: '0 0 14px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '9px 14px', background: head, color: '#fff',
        }}>
          <span style={{ fontSize: 12, opacity: .85 }}>{kind}</span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
          {subtitle && (
            <span style={{ fontSize: 12, opacity: .9 }}>{subtitle}</span>
          )}
          {extra && <span style={{ fontSize: 12, opacity: .9 }}>{extra}</span>}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {(docs || []).map(([label, href]) => (
              <button key={label} type="button"
                      onClick={() => window.open(href, '_blank')}
                      style={panelBtn}>{label}</button>
            ))}
            <button type="button" onClick={() => onCreate('AR')} style={panelBtn}>
              <FiPlus size={11} /> Invoice
            </button>
            <button type="button" onClick={() => onCreate('DC')} style={panelBtn}>
              <FiPlus size={11} /> D/C
            </button>
            <button type="button" onClick={() => onCreate('AP')} style={panelBtn}>
              <FiPlus size={11} /> Cost
            </button>
          </span>
        </div>

        <div className="accounting-table-wrapper">
          <table className="accounting-table">
            <thead>
              <tr>
                <th>Invoice No.</th><th>Party</th>
                <th className="col-number">Revenue</th>
                <th className="col-number">Cost</th>
                <th className="col-number">Balance</th>
                <th>Status</th><th>Post Date</th><th>Invoice Date</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              <Section label="Invoice (A/R)" list={ar} empty="No invoices" />
              <Section label="D/C Note" list={dc} empty="No D/C notes" />
              <Section label="Cost (A/P)" list={ap} empty="No costs" />
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td colSpan="2" className="totals-label">Total</td>
                <td className={`col-number ${rev > 0 ? 'amount-positive' : ''}`}>{formatCurrency(rev)}</td>
                <td className={`col-number ${cost > 0 ? 'amount-positive' : ''}`}>{formatCurrency(cost)}</td>
                <td className={`col-number ${bal >= 0 ? 'amount-positive' : 'amount-negative'}`}>{formatCurrency(bal)}</td>
                <td colSpan="4"></td>
              </tr>
              <tr className="amount-row">
                <td colSpan="2" className="totals-label">Amount</td>
                <td colSpan="2" className={`col-number ${bal >= 0 ? 'amount-positive' : 'amount-negative'}`}>{formatCurrency(bal)}</td>
                <td colSpan="5"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="profit-section">
          <table className="profit-table">
            <thead>
              <tr><th></th>
                <th className="col-number">Amount</th>
                <th className="col-number">Profit Percentage</th>
                <th className="col-number">Profit Margin</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="profit-label">{profitLabel}</td>
                <td className={`col-number ${profit >= 0 ? 'amount-positive' : 'amount-negative'}`}>{formatCurrency(profit)}</td>
                <td className={`col-number ${pct >= 0 ? 'amount-positive' : 'amount-negative'}`}>{pct.toFixed(2)}%</td>
                <td className={`col-number ${margin >= 0 ? 'amount-positive' : 'amount-negative'}`}>{margin.toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const CARD_TINTS = ['#f59e0b', '#3b82f6', '#f97316', '#10b981', '#ef4444',
                      '#8b5cf6', '#0ea5e9', '#84cc16'];

  const HouseCard = ({ id, title, subtitle, tint, count, profit, active }) => (
    <button
      type="button"
      onClick={() => setScope(id)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        padding: '10px 12px', marginBottom: 8, borderRadius: 8,
        border: active ? `2px solid ${tint}` : '1px solid #e2e8f0',
        background: active ? `${tint}14` : '#fff',
        boxShadow: active ? `0 1px 6px ${tint}33` : 'none',
        font: 'inherit',
      }}
    >
      <div style={{
        fontSize: 13, fontWeight: 700, color: '#0f172a',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        <span style={{
          display: 'inline-block', width: 8, height: 8, borderRadius: 2,
          background: tint, marginRight: 8, verticalAlign: 'middle',
        }} />
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>
          {subtitle}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#475569', marginTop: 5,
                    display: 'flex', justifyContent: 'space-between' }}>
        <span>{count} {count === 1 ? 'entry' : 'entries'}</span>
        <span style={{ fontWeight: 600,
                       color: profit >= 0 ? '#059669' : '#dc2626' }}>
          {formatCurrency(profit)}
        </span>
      </div>
    </button>
  );

  const sidebar = scoped && (
    <aside style={{
      width: 232, flex: '0 0 232px', padding: '12px',
      borderLeft: '1px solid #e2e8f0', background: '#f8fafc',
      alignSelf: 'stretch',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b',
                    textTransform: 'uppercase', letterSpacing: '.04em',
                    marginBottom: 8 }}>
        House B/L ({houses.length})
      </div>
      {houses.map((h, i) => {
        const s = perHouse(h);
        return (
          <HouseCard
            key={h.id}
            id={String(h.id)}
            title={houseNo(h)}
            subtitle={h.consignee || h.customer || ''}
            tint={CARD_TINTS[i % CARD_TINTS.length]}
            count={s.count}
            profit={s.profit}
            active={String(scope) === String(h.id)}
          />
        );
      })}

      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #e2e8f0',
                    fontSize: 11, color: '#64748b' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Whole shipment</span>
          <span style={{ fontWeight: 700,
                         color: shipmentProfit >= 0 ? '#059669' : '#dc2626' }}>
            {formatCurrency(shipmentProfit)}
          </span>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="accounting-tab"
         style={scoped ? { display: 'flex', alignItems: 'flex-start' } : undefined}>
      <div style={scoped ? { flex: 1, minWidth: 0 } : undefined}>
      {/* Top Action Buttons */}
      <div className="accounting-toolbar">
        <div className="accounting-toolbar-left">
          <button
            className="btn-create-invoice"
            onClick={() => invoiceInputRef.current?.click()}
            style={{ background: '#059669', marginLeft: '8px' }}
          >
            <FiUpload size={12} /> Upload Invoice
          </button>
          <input
            ref={invoiceInputRef}
            type="file"
            accept=".pdf"
            onChange={handleInvoiceUpload}
            style={{ display: 'none' }}
          />
          <button
            className="btn-create-invoice"
            onClick={() => setShowLoadTP(!showLoadTP)}
            style={{ background: '#7c3aed', marginLeft: '8px' }}
          >
            <FiDownload size={12} /> Default Freight
          </button>
        </div>
        <div className="accounting-toolbar-right">
          <label className="draft-checkbox">
            <input
              type="checkbox"
              checked={includeDraft}
              onChange={(e) => setIncludeDraft(e.target.checked)}
            />
            Include Draft Amount
          </label>
        </div>
      </div>

      {/* Trade Partner Search for Default Freight */}
      {showLoadTP && (
        <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Search Trade Partner:</label>
            <input
              type="text"
              value={tpSearch}
              onChange={(e) => searchTP(e.target.value)}
              placeholder="Type name or code..."
              style={{ flex: 1, maxWidth: '300px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
              autoFocus
            />
            <button className="btn btn-sm btn-secondary" onClick={() => { setShowLoadTP(false); setTpSearch(''); setTpResults([]); }}>
              Cancel
            </button>
          </div>
          {tpResults.length > 0 && (
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff' }}>
              {tpResults.map(tp => (
                <div
                  key={tp.id}
                  onClick={() => !loadingTP && loadDefaultFreight(tp)}
                  style={{
                    padding: '8px 12px', cursor: loadingTP ? 'wait' : 'pointer', fontSize: '13px',
                    borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '12px', alignItems: 'center',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <span style={{ fontWeight: 600, color: '#2563eb', minWidth: '70px' }}>{tp.code}</span>
                  <span style={{ fontWeight: 500 }}>{tp.name}</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>{tp.tp_type}</span>
                  {tp.city && <span style={{ color: '#94a3b8', fontSize: '11px', marginLeft: 'auto' }}>{tp.city}</span>}
                </div>
              ))}
            </div>
          )}
          {tpSearch.length > 0 && tpResults.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '12px' }}>No trade partners found</div>
          )}
        </div>
      )}

      <Panel
        kind={multiHouse ? 'MB/L' : (scoped ? 'MB/L' : '')}
        title={mblNo || 'Master level'}
        subtitle={multiHouse && masterRows.length
          ? `${masterRows.length} ${masterRows.length === 1 ? 'entry is' : 'entries are'} not assigned to a house bill — these print on every house's arrival notice`
          : ''}
        warn={multiHouse && masterRows.length > 0}
        rows={masterRows}
        profitLabel="Total Profit"
        profitValue={scoped ? shipmentProfit : null}
        onCreate={(type) => goToNewCharge(type,
          !multiHouse && currentHouse ? currentNo : '')}
        docs={!multiHouse && currentHouse ? [
          ['Arrival Notice', `/documents/${shipmentType.replace('_', '-')}/${shipmentId}/arrival-notice?hbl_id=${currentHouse.id}`],
          ['Delivery Order', `/documents/${shipmentType.replace('_', '-')}/${shipmentId}/delivery-order?hbl_id=${currentHouse.id}`],
        ] : undefined}
      />

      {multiHouse && currentHouse && (
        <Panel
          kind="HB/L"
          title={currentNo}
          subtitle={currentHouse.consignee || currentHouse.customer
            ? `CUSTOMER : ${currentHouse.consignee || currentHouse.customer}` : ''}
          extra={currentHouse.op ? `OP : ${currentHouse.op}` : ''}
          tint={CARD_TINTS[houses.findIndex(h => String(h.id) === String(scope))
                           % CARD_TINTS.length]}
          rows={currentRows}
          profitLabel="HBL Profit"
          onCreate={(type) => goToNewCharge(type, currentNo)}
          docs={[
            ['Arrival Notice', `/documents/${shipmentType.replace('_', '-')}/${shipmentId}/arrival-notice?hbl_id=${currentHouse.id}`],
            ['Delivery Order', `/documents/${shipmentType.replace('_', '-')}/${shipmentId}/delivery-order?hbl_id=${currentHouse.id}`],
          ]}
        />
      )}

      {/* Memo */}
      <div className="accounting-memo-section">
        <div className="form-section-header">Memo</div>
        <textarea
          className="shipment-memo"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Add memo..."
        />
      </div>

      </div>
      {sidebar}

      {/* Invoice Upload Modal */}
      {invoiceModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', width: '600px', maxHeight: '80vh',
            overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                {invoiceParsing ? 'Parsing Invoice...' : 'Review Invoice — Create A/P'}
              </h3>
              <button onClick={() => { setInvoiceModal(false); setInvoiceData(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px' }}>
                <FiX />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              {invoiceParsing ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <div className="spinner" style={{ display: 'inline-block', marginBottom: '12px' }} />
                  <div>Extracting charges from invoice...</div>
                </div>
              ) : invoiceData ? (
                <>
                  {/* Vendor & Invoice Info */}
                  <div style={{ display: 'flex', gap: '8px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#166534', alignItems: 'center' }}>
                    <FiFileText />
                    <span>Invoice parsed successfully — <strong>{invoiceData.charges.length}</strong> charge(s) found</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px', fontSize: '13px' }}>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Vendor</label>
                      <input type="text" value={invoiceData.vendor || ''} onChange={(e) => handleInvoiceFieldChange('vendor', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Invoice No.</label>
                      <input type="text" value={invoiceData.invoice_no || ''} onChange={(e) => handleInvoiceFieldChange('invoice_no', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Invoice Date</label>
                      <input type="date" value={invoiceData.invoice_date || ''} onChange={(e) => handleInvoiceFieldChange('invoice_date', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Due Date</label>
                      <input type="date" value={invoiceData.due_date || ''} onChange={(e) => handleInvoiceFieldChange('due_date', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                  </div>

                  {/* Reference Numbers */}
                  {invoiceData.reference_nos && invoiceData.reference_nos.length > 0 && (
                    <div style={{ marginBottom: '14px', fontSize: '12px', color: '#475569' }}>
                      <span style={{ fontWeight: 600 }}>References: </span>
                      {invoiceData.reference_nos.map((r, i) => (
                        <span key={i} style={{
                          display: 'inline-block', padding: '2px 8px', background: '#f1f5f9',
                          borderRadius: '4px', marginRight: '6px', marginBottom: '4px', fontSize: '11px',
                        }}>
                          <span style={{ color: '#94a3b8' }}>{r.type}: </span>
                          <strong>{r.value}</strong>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Charge Lines Table */}
                  <div style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                    Charges:
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                        <th style={{ padding: '6px 10px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', width: '130px', textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: '6px 10px', width: '40px', borderBottom: '2px solid #e2e8f0' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.charges.map((ch, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '5px 10px' }}>
                            <input type="text" value={ch.description} onChange={(e) => handleChargeDescChange(i, e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }} />
                          </td>
                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                            <input type="number" step="0.01" value={ch.amount} onChange={(e) => handleChargeAmountChange(i, e.target.value)}
                              style={{ width: '110px', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '5px', textAlign: 'center' }}>
                            <button onClick={() => removeChargeLine(i)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '2px' }}>
                              <FiX />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ padding: '5px 10px' }}>
                          <button onClick={addChargeLine}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '12px', fontWeight: 600, padding: '4px 0' }}>
                            + Add Line
                          </button>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: '14px', borderTop: '2px solid #1e293b' }}>
                          ${invoiceData.total_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td style={{ borderTop: '2px solid #1e293b' }}></td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            {invoiceData && !invoiceParsing && (
              <div style={{
                padding: '14px 20px', borderTop: '1px solid #e2e8f0',
                display: 'flex', gap: '8px', justifyContent: 'flex-end',
              }}>
                <button className="btn" onClick={() => { setInvoiceModal(false); setInvoiceData(null); }}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={createAPFromInvoice} disabled={invoiceCreating}
                  style={{ background: '#059669', borderColor: '#059669' }}>
                  {invoiceCreating ? 'Creating...' : <><FiCheck /> Create A/P</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingTab;
